// Demo engine for /herotest, ported from vovy-site-handoff/reference/index.html.
// Everything is scoped to the page root and torn down by the returned cleanup,
// so the looping demos stop as soon as the route unmounts.

const A = "/herotest-assets";
const LOGO: Record<string, string> = {
  Lovable: `${A}/logos/lovable.svg`, Bolt: `${A}/logos/bolt.svg`, Rork: `${A}/logos/rork.svg`,
  Slack: `${A}/logos/slack.svg`, "VS Code": `${A}/logos/vs-code.svg`, Cursor: `${A}/logos/cursor.svg`,
  Replit: `${A}/logos/replit.svg`, Claude: `${A}/logos/claude.svg`, Figma: `${A}/logos/figma.svg`,
  Supabase: `${A}/logos/supabase.svg`, Stripe: `${A}/logos/stripe.svg`, Vercel: `${A}/logos/vercel.svg`,
  GitHub: `${A}/logos/github.svg`, Chrome: `${A}/logos/chrome.svg`, Safari: `${A}/logos/safari.svg`,
  Notion: `${A}/logos/notion.svg`, Linear: `${A}/logos/linear.svg`, Framer: `${A}/logos/framer.svg`,
  Webflow: `${A}/logos/webflow.svg`, Terminal: `${A}/logos/terminal.svg`,
};
const VL = `${A}/vovy-listening.webp`, VR = `${A}/vovy-rocket.webp`, MASCOT = `${A}/vovy-mascot.webp`;
const ic = (n: string, s = "") => (LOGO[n] ? `<img src="${LOGO[n]}" alt="" ${s}>` : "");

type Step = [tool: string, text: string];

// Thrown out of wait() once the page unmounts, which unwinds every demo loop.
const STOP = Symbol("stop");

// Runs on whatever part of the page `root` holds: /herotest passes the whole
// page, the home page passes just the hero. Sections that aren't present are
// skipped.
export function startHeroTest(root: HTMLElement, onIntroDone?: () => void): () => void {
  let alive = true;
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const cleanups: Array<() => void> = [];

  const $ = <T extends HTMLElement = HTMLElement>(s: string) => root.querySelector(s) as T;
  const $$ = <T extends HTMLElement = HTMLElement>(s: string) => [...root.querySelectorAll(s)] as T[];
  const wait = (ms: number) =>
    new Promise<void>((resolve, reject) => {
      const id = setTimeout(() => {
        timers.delete(id);
        alive ? resolve() : reject(STOP);
      }, ms);
      timers.add(id);
    });
  const loop = (fn: () => Promise<void>) => {
    fn().catch((e) => {
      if (e !== STOP) console.error(e);
    });
  };

  const scopes = $$(".herotest");
  scopes.forEach((el) => el.classList.add("js"));

  // nav turns into a notch
  const nav = $("#nav");
  const onScroll = () => nav.classList.toggle("notch", scrollY > 60);
  addEventListener("scroll", onScroll, { passive: true });
  onScroll();
  cleanups.push(() => removeEventListener("scroll", onScroll));

  // ---- load entrance ----
  // The copy and the MacBook rise in together (incredible.one's quick
  // entrance, timings in herotest.css), then the notch greets you. Reduced
  // motion skips it.
  const hero = $(".hero");
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const mode: "play" | "still" = reduce ? "still" : "play";
  hero.dataset.mode = mode;
  const introMs = mode === "play" ? 900 : 0;
  const introTimer = setTimeout(() => onIntroDone?.(), introMs);
  timers.add(introTimer);

  // reveals
  $$(".faq details").forEach((d, i) => d.style.setProperty("--rd", `${0.14 + i * 0.07}s`));
  const io = new IntersectionObserver(
    (es) => es.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add("in");
        io.unobserve(e.target);
      }
    }),
    { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
  );
  $$(".rv").forEach((el) => io.observe(el));
  cleanups.push(() => io.disconnect());

  // visibility gate so demos only run on screen
  const seen = new Map<Element, boolean>();
  const vio = new IntersectionObserver((es) => es.forEach((e) => seen.set(e.target, e.isIntersecting)), { threshold: 0.1 });
  cleanups.push(() => vio.disconnect());
  const whenSeen = async (el: Element) => {
    vio.observe(el);
    while (!seen.get(el)) await wait(300);
  };

  // ---- wave notch engine ----
  const CUR = `<svg viewBox="0 0 24 24"><path d="M4 2l16 9-7 2-3 7z" fill="#020817" stroke="#fff" stroke-width="1.6" stroke-linejoin="round"/></svg><img src="${MASCOT}" alt="">`;
  $$(".cur").forEach((c) => (c.innerHTML = CUR));

  // Every notch on the page, per Figma "Notch — wave states" (light).
  // MacBook: 362 wide with the camera cutout; desktop display (.desk): 330
  // wide, no cutout. Listening: the glow rises with a simulated mic level
  // (attack ~80ms, release ~250ms). Working: a sweep runs along the bottom
  // edge and fades when a step lands. Sheets (agent card, explainer) open
  // under the pill; the agent chip sits where the close button is.
  function WaveNotch(el: HTMLElement, w: (ms: number) => Promise<void> = wait) {
    const desk = el.classList.contains("desk");
    el.classList.add("wn");
    el.style.setProperty("--W", desk ? "330" : "362");
    el.innerHTML = `<i class="wn-fl l"></i><i class="wn-fl r"></i><div class="wn-b"><i class="wn-haze"></i><i class="wn-lobe l"></i><i class="wn-lobe c"></i><i class="wn-lobe r"></i><i class="wn-core"></i><i class="wn-edge"></i><i class="wn-halo"></i><i class="wn-sweep"></i>${desk ? "" : '<i class="wn-cam"></i>'}<span class="wn-av"><img class="a1" src="${VL}" alt=""><img class="a2" src="${VR}" alt=""><img class="a3" src="${MASCOT}" alt=""></span><span class="wn-x"></span><span class="wn-stop"></span><span class="nchip"><i class="nd"></i>${ic("Claude")}<em>vovy-desktop</em></span><span class="wn-lbl"></span></div><div class="nsheet"></div>`;
    const lbl = el.querySelector(".wn-lbl") as HTMLElement, chip = el.querySelector(".nchip") as HTMLElement;
    const dot = el.querySelector(".nd") as HTMLElement, sheet = el.querySelector(".nsheet") as HTMLElement;
    let lv = 0, tgt = 0, sw = 0, swT = 0, last = performance.now(), talk = false, nx = 0, raf = 0;
    const tick = (now: number) => {
      if (!alive) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (talk && now > nx) {
        tgt = Math.random() < 0.14 ? 0.06 + Math.random() * 0.1 : 0.28 + Math.random() * 0.55;
        nx = now + 90 + Math.random() * 120;
      }
      lv += (tgt - lv) * Math.min(1, dt / (tgt > lv ? 0.08 : 0.25));
      sw += (swT - sw) * Math.min(1, dt / 0.3);
      el.style.setProperty("--lv", lv.toFixed(3));
      el.style.setProperty("--sw", sw.toFixed(3));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    cleanups.push(() => cancelAnimationFrame(raf));
    const setLbl = async (h: string, fade = 240) => {
      const o = lbl.firstChild as HTMLElement | null;
      if (o) {
        o.classList.add("out");
        await w(fade);
      }
      lbl.innerHTML = h ? `<b>${h}</b>` : "";
    };
    const words = (t: string, gap = 0.2) => t.split(" ").map((x, i) => `<span style="animation-delay:${(i * gap).toFixed(2)}s">${x}</span>`).join(" ");
    const state = (st: "listen" | "work" | "done") => (el.dataset.s = st);
    state("listen");
    const api = {
      chip,
      async idle() {
        el.classList.remove("open");
        state("listen");
        swT = 0; tgt = 0; talk = false;
        await setLbl("");
      },
      async say(t: string) {
        el.classList.remove("open");
        state("listen");
        talk = true;
        await w(400);
        await setLbl(words(t));
        await w(t.split(" ").length * 200 + 900);
        talk = false; tgt = 0;
        await w(600);
      },
      // voice-first moments use the same listening wave
      talkBig(t: string) {
        return api.say(t);
      },
      async think(t = "Thinking") {
        el.classList.remove("open");
        state("work");
        swT = 1;
        await setLbl(t);
      },
      // each step: the sweep runs while the label says what it's doing, then
      // fades when the step lands (Figma: "Working · step landed")
      async steps(list: Step[], onStep?: (i: number, step: Step) => void) {
        state("work");
        for (const [i, step] of list.entries()) {
          swT = 1;
          await setLbl(step[1]);
          onStep?.(i, step);
          await w(1500);
          swT = 0;
          await w(700);
        }
      },
      async done(t: string) {
        el.classList.remove("open");
        state("done");
        swT = 0;
        await setLbl(t);
      },
      // fixed-length beats for the hero loop (5s per job, never idle)
      async hear(t: string, ms: number) {
        state("listen");
        swT = 0;
        talk = true;
        const n = t.split(" ").length;
        await setLbl(words(t, Math.min(0.12, (ms - 450) / 1000 / n)), 120);
        await w(ms - 120 - 200);
        talk = false;
        tgt = 0;
        await w(200);
      },
      async run(list: Step[], stepMs: number, onStep?: (i: number, step: Step) => void) {
        state("work");
        swT = 1;
        for (const [i, step] of list.entries()) {
          await setLbl(step[1], 140);
          onStep?.(i, step);
          await w(stepMs - 140);
        }
      },
      async finish(t: string, ms: number) {
        state("done");
        await setLbl(t, 120);
        await w(ms - 120);
      },
      // resting notch with an agent chip where the close button sits
      async rest(d: "work" | "done") {
        el.classList.add("chip");
        dot.classList.toggle("g", d === "done");
        await api.idle();
      },
      setChip(d: "work" | "done") {
        dot.classList.toggle("g", d === "done");
      },
      unchip() {
        el.classList.remove("chip");
      },
      card(html: string, sw = 600) {
        el.style.setProperty("--shw", String(sw));
        sheet.innerHTML = html;
        el.classList.add("open");
        return sheet;
      },
      close() {
        el.classList.remove("open");
      },
    };
    return api;
  }
  const toast = async (t: HTMLElement, logo: string, msg: string, ms = 2200) => {
    t.innerHTML = ic(logo) + msg;
    t.classList.add("on");
    await wait(ms);
    t.classList.remove("on");
  };
  // Vovy rides beside the cursor; near the bottom or right edge of the
  // screen he swaps to the other side so he's never cut off
  const moveCur = async (c: HTMLElement, x: string, y: string, click?: boolean) => {
    c.style.left = x;
    c.style.top = y;
    c.classList.toggle("up", parseFloat(y) > 72);
    c.classList.toggle("lf", parseFloat(x) > 86);
    await wait(1150);
    if (click) {
      c.classList.add("click");
      await wait(160);
      c.classList.remove("click");
    }
  };

  // small inline icons for the hero's app windows (stroke style, like SF Symbols)
  const ico = (d: string, fill = false) =>
    `<svg viewBox="0 0 24 24" ${fill ? 'fill="currentColor"' : 'fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"'}>${d}</svg>`;
  const SV = {
    gpt: ico('<path d="M12 3.2a4.3 4.3 0 0 1 4.1 3 4.3 4.3 0 0 1 3.3 6 4.3 4.3 0 0 1-2.6 7.1 4.3 4.3 0 0 1-6.6 1.5 4.3 4.3 0 0 1-6-3.3 4.3 4.3 0 0 1-1.5-6.6 4.3 4.3 0 0 1 3.3-6 4.3 4.3 0 0 1 6-1.7z"/><path d="M9 9.5l3-1.7 3 1.7v3.4l-3 1.7-3-1.7z"/>'),
    sidebar: ico('<rect x="3" y="4" width="18" height="16" rx="3"/><path d="M9 4v16"/>'),
    edit: ico('<path d="M4 20h4L19 9l-4-4L4 16z"/><path d="M14 6l4 4"/>'),
    search: ico('<circle cx="11" cy="11" r="6.5"/><path d="M20 20l-4.2-4.2"/>'),
    lib: ico('<rect x="4" y="4" width="16" height="16" rx="3"/><path d="M4 15l4.5-4.5L13 15l3-3 4 4"/>'),
    share: ico('<path d="M12 15V4M8 8l4-4 4 4"/><path d="M5 13v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5"/>'),
    code: ico('<path d="M9 8l-4 4 4 4M15 8l4 4-4 4"/>'),
    copy: ico('<rect x="8" y="8" width="12" height="12" rx="2.5"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/>'),
    up: ico('<path d="M7 11v9H4v-9zM7 11l4-7a2 2 0 0 1 3 1.7V9h5a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 17.8 20H7"/>'),
    down: ico('<path d="M7 13V4H4v9zM7 13l4 7a2 2 0 0 0 3-1.7V15h5a2 2 0 0 0 2-2.3l-1.2-7A2 2 0 0 0 17.8 4H7"/>'),
    redo: ico('<path d="M20 12a8 8 0 1 1-2.3-5.7L20 8.5"/><path d="M20 4v4.5h-4.5"/>'),
    plus: ico('<path d="M12 5v14M5 12h14"/>'),
    mic: ico('<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"/>'),
    arrow: ico('<path d="M12 19V5M6 11l6-6 6 6"/>'),
    check: ico('<path d="M5 12.5l4.5 4.5L19 7.5"/>'),
    signal: ico('<rect x="3" y="15" width="3" height="5" rx="1"/><rect x="8.5" y="12" width="3" height="8" rx="1"/><rect x="14" y="8.5" width="3" height="11.5" rx="1"/><rect x="19.5" y="5" width="3" height="15" rx="1" opacity=".35"/>', true),
    wifi: ico('<path d="M12 19.5l2.6-3.1a4 4 0 0 0-5.2 0zM6.4 12.9a8.4 8.4 0 0 1 11.2 0l1.7-2a11 11 0 0 0-14.6 0zM2.9 9a13.6 13.6 0 0 1 18.2 0L22.8 7A16.2 16.2 0 0 0 1.2 7z"/>', true),
    battery: ico('<rect x="1.5" y="6.5" width="19" height="11" rx="3" fill="none" stroke="currentColor" stroke-width="1.2" opacity=".5"/><rect x="3.2" y="8.2" width="12.5" height="7.6" rx="1.6"/><path d="M21.8 10v4a2 2 0 0 0 0-4z" opacity=".5"/>', true),
    today: ico('<rect x="4" y="5" width="16" height="15" rx="3"/><path d="M4 10h16M9 3v4M15 3v4"/><path d="M9 14.5l2 2 4-4"/>'),
    chart: ico('<path d="M5 20V10M12 20V4M19 20v-7"/>'),
    gear: ico('<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/>'),
    key: ico('<circle cx="8" cy="15" r="4"/><path d="M10.8 12.2L19 4M15.5 7.5l2.5 2.5M17.5 5.5l2 2"/>'),
    link: ico('<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/>'),
    drop: ico('<path d="M12 3.5s6 6.4 6 10.5a6 6 0 0 1-12 0C6 9.9 12 3.5 12 3.5z"/>', true),
    book: ico('<path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5zM4 20.5A2.5 2.5 0 0 0 6.5 23H20v-5"/>'),
    walk: ico('<circle cx="13" cy="4.5" r="2"/><path d="M10 21l2.5-6.5L15 17v4M8 12l3-4.5h3l2 3.5 3 1M12.5 14.5L11 9"/>'),
    leaf: ico('<path d="M5 19c0-8 6-14 15-14 0 9-6 15-14 15M5 19l7-7"/>'),
    table: ico('<rect x="3.5" y="4.5" width="17" height="15" rx="2.5"/><path d="M3.5 9.5h17M9.5 9.5v10"/>'),
  };

  // ---- hero demo: a ~30s loop ----
  // 0  intro: clean desktop; Vovy falls from the headline to the middle of the
  //    screen, shrinking, then flies up into the notch as it pops in, and says hi
  // 1  ChatGPT: writes a detailed prompt, builds a SaaS dashboard, opens it full screen
  // 2  Claude Code in Terminal: builds a Cal AI style calorie app, runs it in Simulator
  // 3  explains the app's Supabase schema: blue highlight box + micro lesson
  loop(async () => {
    const n = WaveNotch($("#n1")), t = $("#t1"), c = $("#c1"), scr = $("#hscreen");
    const wins = $("#hwins"), dock = $("#hdock"), scene = $("#hscene"), notch = $("#n1");
    const bub = $("#nbub"), bvp = bub.querySelector(".nbub-vp") as HTMLElement;
    const blines = [...bub.querySelectorAll<HTMLElement>(".nbub-line")], keys = [...bub.querySelectorAll<HTMLElement>("kbd")];
    const L = (x: string) => LOGO[x];
    const img = (src: string) => `<img src="${src}" alt="">`;
    const fav = (letter: string, bg: string) => `<i class="fav" style="background:${bg}">${letter}</i>`;
    // Chrome window: tab strip + toolbar + page
    const chrome = (favicon: string, title: string, url: string, page: string) =>
      `<div class="cw-tabs"><i></i><i></i><i></i><span class="cw-tab">${favicon}<span>${title}</span><em>×</em></span><span class="cw-plus">+</span></div>` +
      `<div class="cw-bar"><span class="cw-nav"><b>‹</b><b>›</b><b>↻</b></span><span class="cw-omni"><svg viewBox="0 0 12 12"><path d="M3.5 5V3.8a2.5 2.5 0 015 0V5" fill="none" stroke="currentColor" stroke-width="1.2"/><rect x="2.5" y="5" width="7" height="5" rx="1.2" fill="currentColor"/></svg>${url}</span><span class="cw-ext">⋮</span><span class="cw-prof">J</span></div>` +
      `<div class="cw-page">${page}</div>`;
    // Terminal.app window
    const term = (title: string, body: string) =>
      `<div class="tm-title"><i></i><i></i><i></i><span>${title}</span></div><div class="cc-body">${body}</div>`;
    const bounce = (app: string) => {
      const icon = dock.querySelector(`[data-app="${app}"]`);
      if (!icon) return;
      icon.classList.remove("bounce");
      void (icon as HTMLElement).offsetWidth;
      icon.classList.add("bounce", "run");
    };
    // opening an app: the cursor goes down to its Dock icon and clicks it,
    // then the window opens (apps without an icon, like Simulator, just open)
    const open = async (cls: string, html: string, app?: string) => {
      const icon = app ? dock.querySelector(`[data-app="${app}"]`) : null;
      if (icon) await click(icon, 0.5, 0.45);
      const win = document.createElement("div");
      win.className = `mw hw ${cls}`;
      win.innerHTML = html;
      wins.appendChild(win);
      requestAnimationFrame(() => win.classList.add("open"));
      if (app) bounce(app);
      return win;
    };
    const closeApps = async () => {
      scene.classList.remove("fs");
      const old = [...wins.querySelectorAll(".hw")];
      old.forEach((wEl) => wEl.classList.add("closing"));
      dock.querySelectorAll(".run:not([data-app=finder])").forEach((d) => d.classList.remove("run", "bounce"));
      await wait(380);
      old.forEach((wEl) => wEl.remove());
    };
    const type = async (el: Element, text: string, ms: number) => {
      for (let i = 1; i <= text.length; i++) {
        el.textContent = text.slice(0, i);
        await wait(ms);
      }
    };
    // long prompts: type the whole thing in `total` ms, a few characters per frame
    const typeIn = async (el: Element, text: string, total: number) => {
      const steps = Math.max(1, Math.round(total / 30));
      for (let k = 1; k <= steps; k++) {
        el.textContent = text.slice(0, Math.round((text.length * k) / steps));
        el.scrollTop = el.scrollHeight;
        await wait(30);
      }
    };
    // the demo cursor, aimed at a point inside an element (percent of the screen)
    const at = (el: Element, fx: number, fy: number): [string, string] => {
      const d = scene.getBoundingClientRect(), r = el.getBoundingClientRect();
      return [((r.left + r.width * fx - d.left) / d.width) * 100 + "%", ((r.top + r.height * fy - d.top) / d.height) * 100 + "%"];
    };
    const click = async (el: Element, fx = 0.5, fy = 0.5) => moveCur(c, ...at(el, fx, fy), true);
    // keep the cursor (and the Vovy riding it) moving around while a step plays
    // out: it drifts between points on these elements until stopped. Call the
    // returned stop() before the next click so the two don't fight.
    const roam = (pts: Array<[Element | null | undefined, number, number]>) => {
      let on = true;
      (async () => {
        for (let i = 0; on; i++) {
          const [el, fx, fy] = pts[i % pts.length];
          if (el && el.isConnected) await moveCur(c, ...at(el, fx, fy));
          if (on) await wait(220);
        }
      })().catch(() => {});
      return () => { on = false; };
    };
    const say = (logo: string, msg: string, ms = 1300) => {
      t.innerHTML = ic(logo) + msg;
      t.classList.add("on");
      const id = setTimeout(() => t.classList.remove("on"), ms);
      timers.add(id);
    };
    const fitBubble = (i: number) => (bvp.style.width = `${blines[i].scrollWidth + 1}px`);

    // 0: the intro. Camera in, notch drops, hi + keycaps, camera out.
    const drop = $("#ndrop");
    const intro = async () => {
      notch.classList.remove("pop");
      notch.classList.add("hid", "noav");
      await n.idle();
      moveCur(c, "28%", "74%").catch(() => {});
      // three positions: 1 the headline Vovy, 2 the middle of the screen, 3 the notch
      // (he shrinks on the way down, and again into the notch)
      const hv = $("#hvovy");
      const hr = hero.getBoundingClientRect(), dr = scene.getBoundingClientRect();
      const av = notch.querySelector(".wn-av")!.getBoundingClientRect();
      const hb = hv?.getBoundingClientRect();
      const midSize = av.height * 2.6;
      const size = hb ? Math.max(hb.height, midSize) : midSize;
      const top = hb ? { x: hb.left + hb.width / 2 - hr.left - size / 2, y: hb.top + hb.height / 2 - hr.top - size / 2 } : { x: dr.left + dr.width / 2 - hr.left - size / 2, y: -size - 20 };
      const k0 = hb ? hb.height / size : 1;
      const mid = { x: dr.left + dr.width / 2 - hr.left - size / 2, y: dr.top + dr.height * 0.42 - hr.top - size / 2 };
      const end = { x: av.left + av.width / 2 - hr.left - size / 2, y: av.top + av.height / 2 - hr.top - size / 2 };
      const T = (p: { x: number; y: number }, k = 1) => `translate(${p.x}px, ${p.y}px) scale(${k})`;
      Object.assign(drop.style, { width: `${size}px`, height: `${size}px`, opacity: "1" });
      hv?.classList.add("out");
      const fall = drop.animate([{ transform: T(top, k0) }, { transform: T(mid, midSize / size) }], { duration: 1100, easing: "cubic-bezier(.33,0,.15,1)", fill: "forwards" });
      await wait(1100);
      // a beat in the middle of the screen
      await wait(550);
      // up into the notch, which pops in to catch him
      const rise = drop.animate([{ transform: T(mid, midSize / size) }, { transform: T(end, av.height / size) }], { duration: 750, easing: "cubic-bezier(.65,0,.35,1)", fill: "forwards" });
      await wait(380);
      notch.classList.remove("hid");
      notch.classList.add("pop");
      await wait(370);
      notch.classList.remove("noav");
      drop.style.opacity = "0";
      fall.cancel();
      rise.cancel();
      // and back in the headline: a plain fade, no slide
      await wait(200);
      hv?.classList.remove("out");
      // in the notch: "Hey! I'm Vovy.", then "Press fn + ctrl to say hi"
      blines.forEach((l, k) => l.classList.toggle("on", k === 0));
      fitBubble(0);
      bub.classList.add("on");
      await wait(1150);
      blines[0].classList.remove("on");
      blines[1].classList.add("on");
      fitBubble(1);
      await wait(400);
      keys[0].classList.add("press");
      await wait(140);
      keys[1].classList.add("press");
      await wait(650);
      keys.forEach((k) => k.classList.remove("press"));
      await wait(250);
      bub.classList.add("gone");
      await wait(300);
      bub.classList.remove("on", "gone");
    };

    // the Pulse dashboard: KPI cards, revenue area chart, signups bars, customers table
    const DASH = (cls = "") => {
      const bars = [38, 52, 44, 61, 58, 73, 69, 84, 78, 92, 88, 100];
      const rows: Array<[string, string, string, string, string]> = [
        ["Acme Robotics", "Scale", "$2,400", "Active", "#22C55E"],
        ["Northwind", "Growth", "$890", "Active", "#22C55E"],
        ["Lumen Health", "Scale", "$2,400", "Trial", "#F59E0B"],
        ["Kite & Co.", "Starter", "$290", "Active", "#22C55E"],
      ];
      return `<div class="pd ${cls}">
        <aside class="pd-side"><b class="pd-logo"><i>⌁</i>Pulse</b>
          <span class="on">${SV.chart}Overview</span><span>${SV.today}Revenue</span><span>${SV.search}Customers</span><span>${SV.gear}Settings</span>
          <div class="pd-plan"><b>Scale plan</b><small>12 of 15 seats</small><i><em></em></i></div></aside>
        <main class="pd-main">
          <header class="pd-top"><div><h6>Overview</h6><small>Last 30 days</small></div><span class="pd-range">Sep 3 – Oct 3 ⌄</span><span class="pd-av">J</span></header>
          <div class="pd-kpis">
            <div class="pd-kpi"><small>MRR</small><b>$48,210</b><em class="up">↑ 12.4%</em></div>
            <div class="pd-kpi"><small>Active users</small><b>3,214</b><em class="up">↑ 8.1%</em></div>
            <div class="pd-kpi"><small>New signups</small><b>642</b><em class="up">↑ 21.7%</em></div>
            <div class="pd-kpi"><small>Churn</small><b>2.1%</b><em class="dn">↓ 0.4%</em></div>
          </div>
          <div class="pd-charts">
            <div class="pd-card pd-rev"><div class="pd-ch"><b>Revenue</b><small>$48.2k this month</small></div>
              <svg viewBox="0 0 300 110" preserveAspectRatio="none"><defs><linearGradient id="pdg${cls}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#6366F1" stop-opacity=".35"/><stop offset="1" stop-color="#6366F1" stop-opacity="0"/></linearGradient></defs>
                <path class="pd-grid" d="M0 27H300M0 55H300M0 83H300"/>
                <path class="pd-area" d="M0 92 C20 88 35 80 55 82 S90 70 110 66 S150 58 170 52 S210 40 230 36 S270 22 300 14 V110 H0Z" fill="url(#pdg${cls})"/>
                <path class="pd-line" d="M0 92 C20 88 35 80 55 82 S90 70 110 66 S150 58 170 52 S210 40 230 36 S270 22 300 14"/></svg></div>
            <div class="pd-card pd-sign"><div class="pd-ch"><b>Signups</b><small>by week</small></div>
              <div class="pd-bars">${bars.map((h, k) => `<i style="height:${h}%;transition-delay:${(k * 40).toFixed(0)}ms"></i>`).join("")}</div></div>
          </div>
          <div class="pd-card pd-table"><div class="pd-ch"><b>Recent customers</b><small>View all</small></div>
            <div class="pd-tr pd-th"><span>Customer</span><span>Plan</span><span>MRR</span><span>Status</span></div>
            ${rows.map(([c, p, m, st, col]) => `<div class="pd-tr"><span><i class="pd-co">${c[0]}</i>${c}</span><span>${p}</span><span>${m}</span><span><em style="color:${col}">● ${st}</em></span></div>`).join("")}
          </div>
        </main></div>`;
    };

    // 1: ChatGPT builds a SaaS dashboard in canvas, then it opens full screen
    const chatgptDashboard = async () => {
      const PROMPT = [
        "Build a SaaS analytics dashboard called Pulse with React, Tailwind and shadcn/ui.",
        "",
        "Layout: a left sidebar with the Pulse logo, Overview, Revenue, Customers and Settings, plus a plan usage card at the bottom. A top bar with the page title, a date range picker and my avatar.",
        "",
        "Overview: four KPI cards for MRR, active users, new signups and churn, each with the change vs last month in green or red. Below them a revenue area chart for the last 30 days and a weekly signups bar chart side by side.",
        "",
        "Customers: a table of recent customers with name, plan, MRR and a colored status badge.",
        "",
        "Pull the data from Supabase and Stripe, keep it clean and minimal with lots of white space, and make it fully responsive.",
      ].join("\n");
      await n.hear("build me a SaaS dashboard", 900);
      await n.think("Opening the ChatGPT app");
      const w = await open("cg gxapp", `<div class="gx-title"><i></i><i></i><i></i></div>`+
        `<div class="gx">
          <aside class="gx-side">
            <div class="gx-sh"><i>${SV.sidebar}</i><i>${SV.edit}</i></div>
            <span class="gx-it">${SV.edit}New chat</span><span class="gx-it">${SV.search}Search chats</span><span class="gx-it">${SV.lib}Library</span>
            <small>Chats</small>
            <span class="gx-it on">Pulse SaaS dashboard</span><span class="gx-it">Weekend in Lisbon</span><span class="gx-it">Resume bullet points</span><span class="gx-it">Explain React hooks</span>
            <div class="gx-user"><i>J</i><span>Jack<small>Plus</small></span></div>
          </aside>
          <main class="gx-main">
            <header class="gx-top"><b>ChatGPT <em>⌄</em></b><span class="gx-share">${SV.share}Share</span></header>
            <div class="gx-thread">
              <h5 class="gx-hi">What can I help with?</h5>
              <div class="gx-me"></div>
              <div class="gx-ai"><p>Here's <b>Pulse</b>, a SaaS analytics dashboard with KPI cards, revenue and signup charts, and a customers table.</p>
                <div class="gx-canvas"><div class="gx-cbar"><span>${SV.code}<b>Pulse Dashboard</b></span><span class="gx-seg"><i>Code</i><i class="on">Preview</i></span></div>
                  ${DASH("mini")}</div>
                <div class="gx-acts">${SV.copy}${SV.up}${SV.down}${SV.redo}</div></div>
            </div>
            <div class="gx-comp"><i class="gx-plus">${SV.plus}</i><span class="gx-text" data-ph="Ask anything"></span><i class="gx-mic">${SV.mic}</i><b class="gx-send">${SV.arrow}</b></div>
            <small class="gx-foot">ChatGPT can make mistakes. Check important info.</small>
          </main>
        </div>`, "chatgpt");
      await wait(450);
      const text = w.querySelector(".gx-text")!, send = w.querySelector(".gx-send")!;
      await n.think("Writing the prompt");
      await click(text, 0.15);
      w.classList.add("typing");
      const gm = w.querySelector(".gx-main"), gs = w.querySelector(".gx-side");
      let stop = roam([[gm, 0.62, 0.3], [gs, 0.5, 0.25], [gm, 0.3, 0.55], [text, 0.8, 0.5]]);
      await typeIn(text, PROMPT, 3800);
      stop();
      await click(send);
      w.querySelector(".gx-me")!.textContent = PROMPT;
      text.textContent = "";
      w.classList.remove("typing");
      w.classList.add("sent");
      await n.think("Building the dashboard");
      await wait(450);
      w.classList.add("built");
      // the preview builds up part by part, the cursor following along
      const q = (sel: string) => w.querySelector(`.gx-canvas ${sel}`);
      stop = roam([[q(".pd-side"), 0.5, 0.2], [q(".pd-kpis"), 0.3, 0.5], [q(".pd-rev"), 0.6, 0.5], [q(".pd-table"), 0.4, 0.5], [q(".pd-sign"), 0.5, 0.5]]);
      for (const part of ["side", "kpis", "charts", "table"]) {
        await wait(420);
        w.classList.add(`b-${part}`);
      }
      await wait(1100);
      stop();
      // publish and open it full screen
      await n.think("Opening it full screen");
      await click(w.querySelector(".gx-canvas .pd")!);
      const fw = await open("cw fsw", chrome(`<i class="fav" style="background:#6366F1">⌁</i>`, "Pulse · Overview", "chatgpt.com/canvas/shared/pulse-dashboard", DASH("full")));
      scene.classList.add("fs"); // full screen: menu bar and Dock slide away
      const f = (sel: string) => fw.querySelector(sel);
      stop = roam([[f(".pd-kpi"), 0.5, 0.5], [f(".pd-rev"), 0.75, 0.4], [f(".pd-sign"), 0.5, 0.6], [f(".pd-table"), 0.3, 0.6]]);
      await wait(2400);
      say("ChatGPT", "Your dashboard is ready");
      await n.finish("Done", 900);
      stop();
    };

    // Claude Code in Terminal: welcome box, input box, tool calls, status line
    const ccWindow = () =>
      term("plately — claude — 92×28",
        `<div class="ccx">
          <div class="cc-box"><b>✻</b> Welcome to <strong>Claude Code</strong>!<p>/help for help, /status for your current setup</p><p>cwd: /Users/jack/plately</p></div>
          <div class="cc-hist"></div>
          <div class="cc-status"><b>✻</b> <span>Working…</span> <em>(esc to interrupt)</em></div>
          <div class="cc-in"><span class="cc-gt">&gt;</span><span class="cc-type"></span><i class="caret"></i></div>
          <div class="cc-hint"><span>? for shortcuts</span><span>Opus 4.1</span></div>
        </div>`);
    // like a real terminal, keep the newest lines in view
    const ccScroll = (w: Element) => {
      const c = w.querySelector(".ccx")!;
      c.scrollTop = c.scrollHeight;
    };
    const ccSubmit = (w: Element) => {
      const t = w.querySelector(".cc-type")!;
      const you = document.createElement("p");
      you.className = "cc-you";
      you.textContent = `> ${t.textContent}`;
      w.querySelector(".cc-hist")!.appendChild(you);
      t.textContent = "";
      ccScroll(w);
    };
    const ccLog = (w: Element, html: string) => {
      w.querySelector(".cc-hist")!.insertAdjacentHTML("beforeend", html);
      ccScroll(w);
      setTimeout(() => ccScroll(w), 300); // after the line's rise-in
    };
    const ccTool = (name: string, arg: string, result: string) =>
      `<p class="cc-tool"><b>⏺</b> <strong>${name}</strong>(${arg})</p><p class="cc-res">  ⎿  ${result}</p>`;
    const ccStatus = (w: Element, label: string | null) => {
      const st = w.querySelector<HTMLElement>(".cc-status")!;
      st.classList.toggle("on", !!label);
      if (label) st.querySelector("span")!.textContent = label;
      ccScroll(w);
    };

    // 2: Claude Code in Terminal builds an iOS app, runs it in Simulator
    const claudeIos = async () => {
      await n.hear("build a calorie tracker app like Cal AI", 900);
      await n.think("Opening Claude in Terminal");
      const w = await open("tm", ccWindow(), "terminal");
      await wait(350);
      let stop = roam([[w, 0.7, 0.3], [w, 0.35, 0.6], [w, 0.8, 0.75], [w, 0.5, 0.4]]);
      await typeIn(w.querySelector(".cc-type")!, [
        "Build an iOS calorie tracker like Cal AI in SwiftUI, called Plately.",
        "",
        "Home screen: today's calories left in a big progress ring, three cards for protein, carbs and fat left with their own colored rings, a week strip across the top with a daily streak counter, and a 'Recently uploaded' list showing each meal's photo, name, time, calories and macros.",
        "",
        "Logging: a floating + button opens the camera. Snap a photo of a meal, send it to an AI vision model, and fill in the name, calories and macros automatically. Let me edit any value before saving.",
        "",
        "Data: store users, meals and macros in Supabase with row level security so people only see their own meals. Sign in with Apple.",
        "",
        "Style it like a clean Apple app: SF Pro, white cards on light gray, rounded corners, haptics on save. Then build it and run it on the iPhone 16 Pro simulator.",
      ].join("\n"), 3800);
      ccSubmit(w);
      await n.think("Writing the SwiftUI app");
      ccStatus(w, "Planning…");
      await wait(300);
      ccLog(w, `<p class="cc-say"><b>⏺</b> I'll build <strong>Plately</strong>: snap a photo of your meal and it logs the calories and macros.</p>`);
      ccStatus(w, "Writing…");
      for (const [f, lines] of [["Plately/PlatelyApp.swift", 18], ["Plately/Meal.swift", 44], ["Plately/HomeView.swift", 128]] as const) {
        await wait(320);
        ccLog(w, ccTool("Write", f, `Wrote ${lines} lines to ${f}`));
      }
      await n.think("Running it in Simulator");
      ccStatus(w, "Building…");
      await wait(300);
      ccLog(w, ccTool("Bash", "xcodebuild -scheme Plately -destination 'name=iPhone 16 Pro' build", `<span class="ok">** BUILD SUCCEEDED **</span>`));
      ccStatus(w, null);
      stop();
      const ring = (pct: number, color: string, inner: string, cls = "") =>
        `<div class="cal-ring ${cls}"><svg viewBox="0 0 36 36"><circle cx="18" cy="18" r="15.5"/><circle class="v" cx="18" cy="18" r="15.5" style="stroke:${color};stroke-dasharray:${(pct * 97.4).toFixed(1)} 97.4"/></svg>${inner}</div>`;
      const macro = (n: string, label: string, pct: number, color: string, icon: string) =>
        `<div class="cal-mac"><b>${n}</b><small>${label}</small>${ring(pct, color, `<i>${icon}</i>`, "sm")}</div>`;
      const meal = (emoji: string, bg: string, name: string, kcal: number, p: number, c: number, f: number, time: string) =>
        `<div class="cal-meal"><i class="cal-ph" style="background:${bg}">${emoji}</i><div><span class="cal-mh"><b>${name}</b><small>${time}</small></span><span class="cal-kc">🔥 ${kcal} calories</span><span class="cal-mm"><em>🍗 ${p}g</em><em>🌾 ${c}g</em><em>🥑 ${f}g</em></span></div></div>`;
      await open("sim",
        `<div class="simw"><div class="sim-label"><b>iPhone 16 Pro</b> iOS 18.2</div>
          <div class="iph"><div class="iph-scr cal">
            <div class="ios-sb"><b>9:41</b><span class="ios-island"></span><span class="ios-ic">${SV.signal}${SV.wifi}${SV.battery}</span></div>
            <div class="cal-top"><b>🍏 Plately</b><span class="cal-streak">🔥 15</span></div>
            <div class="cal-days">${["S", "M", "T", "W", "T", "F", "S"].map((d, k) => `<span class="${k === 2 ? "on" : ""}"><small>${d}</small><b>${27 + k > 30 ? 27 + k - 30 : 27 + k}</b></span>`).join("")}</div>
            <div class="cal-main"><div><b>1,250</b><small>Calories left</small></div>${ring(0.42, "#111", "<i>🔥</i>")}</div>
            <div class="cal-macs">${macro("98g", "Protein left", 0.38, "#F87171", "🍗")}${macro("140g", "Carbs left", 0.55, "#FBBF24", "🌾")}${macro("36g", "Fats left", 0.3, "#60A5FA", "🥑")}</div>
            <h6 class="cal-h">Recently uploaded</h6>
            ${meal("🥗", "#DCFCE7", "Chicken Caesar salad", 460, 38, 18, 24, "12:41")}${meal("🍳", "#FEF3C7", "Avocado toast & eggs", 520, 24, 42, 28, "8:15")}${meal("🍣", "#FFE4E6", "Salmon poke bowl", 610, 36, 64, 18, "Yesterday")}
            <b class="cal-fab">+</b>
            <div class="ios-tab cal-tab"><span class="on">${SV.today}Home</span><span>${SV.chart}Progress</span><span>${SV.gear}Settings</span></div>
            <i class="ios-home"></i>
          </div></div></div>`);
      const phone = wins.querySelector(".iph-scr");
      stop = roam([[phone, 0.5, 0.3], [phone, 0.6, 0.48], [phone, 0.4, 0.7], [phone, 0.85, 0.82]]);
      await wait(1300);
      say("Claude", "Plately runs on iPhone");
      await n.finish("Done", 900);
      stop();
    };

    // 3: explains the Supabase schema with a highlight box + micro lesson.
    // Relation lines, the highlight and the lesson are placed from the real
    // rows after layout, so they always line up.
    const explainDb = async () => {
      await n.hear("what does my database do?", 900);
      await n.think("Reading your schema");
      const col = (name: string, type: string, key?: "pk" | "fk", nul = false) =>
        `<span class="sbc${key ? ` ${key}` : ""}" data-col="${name}"><i>${key === "pk" ? SV.key : key === "fk" ? SV.link : nul ? "◇" : "◆"}</i><em>${name}</em><small>${type}</small></span>`;
      const tbl = (id: string, name: string, cols: string) => `<div class="sbt" id="${id}"><b>${SV.table}${name}</b>${cols}</div>`;
      const w = await open("sb", chrome(img(L("Supabase")), "Schema Visualizer | Supabase", "supabase.com/dashboard/project/plately/database/schemas",
        `<div class="sup"><div class="sup-rail">${img(L("Supabase"))}<i></i><i></i><i class="on"></i><i></i><i></i></div><div class="sup-col">
          <div class="sup-top"><span>jack's org</span><em>/</em><b>plately</b><span class="sup-pill">Free</span><em>/</em><span class="sup-br">⎇ main</span></div>
          <div class="sbv-bar"><span class="sbv-sel">schema <b>public</b> ⌄</span><span class="sbv-sp"></span><span class="sbv-btn">Auto layout</span><span class="sbv-btn">Copy as SQL</span><span class="sbv-zoom">−&nbsp; 100% &nbsp;+</span></div>
          <div class="sbv">
            <svg class="sbv-lines"></svg>
            ${tbl("t-users", "users", col("id", "uuid", "pk") + col("email", "text") + col("full_name", "text", undefined, true) + col("created_at", "timestamptz"))}
            ${tbl("t-habits", "meals", col("id", "uuid", "pk") + col("user_id", "uuid", "fk") + col("name", "text") + col("calories", "int4") + col("eaten_at", "timestamptz"))}
            ${tbl("t-checks", "macros", col("id", "uuid", "pk") + col("meal_id", "uuid", "fk") + col("protein_g", "int4") + col("carbs_g", "int4") + col("fat_g", "int4"))}
            <i class="sbv-hl"></i>
            <div class="sbv-lesson"><div class="lc-h"><img src="${MASCOT}" alt=""><b>Why meals has a user_id</b></div><p>Each meal belongs to one person. <code>user_id</code> points at a row in <code>users</code>, so the app only ever loads your own meals.</p><div class="ac-btns"><span class="pri">Got it</span><span>Show me more</span></div></div>
          </div></div></div>`), "chrome");
      await wait(500);
      const cv = w.querySelector<HTMLElement>(".sbv")!, svg = w.querySelector<SVGSVGElement>(".sbv-lines")!;
      const layout = () => {
        const C = cv.getBoundingClientRect();
        const r = (sel: string) => {
          const b = w.querySelector(sel)!.getBoundingClientRect();
          return { l: b.left - C.left, t: b.top - C.top, r: b.right - C.left, b: b.bottom - C.top, m: b.top - C.top + b.height / 2 };
        };
        svg.setAttribute("viewBox", `0 0 ${C.width} ${C.height}`);
        const uid = r('#t-users [data-col="id"]'), hfk = r('#t-habits [data-col="user_id"]');
        const hid = r('#t-habits [data-col="id"]'), cfk = r('#t-checks [data-col="meal_id"]');
        const curve = (x1: number, y1: number, x2: number, y2: number) => {
          const dx = Math.max(24, Math.abs(x2 - x1) / 2);
          return `M${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
        };
        svg.innerHTML = `<path d="${curve(uid.r, uid.m, hfk.l, hfk.m)}"/><path d="${curve(hid.r, hid.m, cfk.l, cfk.m)}"/>` +
          [[uid.r, uid.m], [hfk.l, hfk.m], [hid.r, hid.m], [cfk.l, cfk.m]].map(([x, y]) => `<circle cx="${x}" cy="${y}" r="2.4"/>`).join("");
        // highlight hugs the meals.user_id row
        const hl = w.querySelector<HTMLElement>(".sbv-hl")!;
        Object.assign(hl.style, { left: `${hfk.l - 4}px`, top: `${hfk.t - 3}px`, width: `${hfk.r - hfk.l + 8}px`, height: `${hfk.b - hfk.t + 6}px` });
        // lesson sits under the users table, fully inside the canvas
        const ut = r("#t-users"), ls = w.querySelector<HTMLElement>(".sbv-lesson")!;
        const lw = Math.min(C.width * 0.4, C.width - ut.l - 12);
        Object.assign(ls.style, { left: `${ut.l}px`, width: `${lw}px`, top: `${Math.min(ut.b + 14, C.height - ls.offsetHeight - 12)}px` });
      };
      layout();
      const stop = roam([[w.querySelector("#t-users"), 0.6, 0.4], [w.querySelector("#t-checks"), 0.5, 0.5]]);
      await wait(1500);
      stop();
      await click(w.querySelector('#t-habits [data-col="user_id"]')!, 0.35);
      await n.think("Explaining");
      layout();
      w.classList.add("hl");
      await wait(700);
      w.classList.add("lesson");
      moveCur(c, ...at(w.querySelector(".sbv-lesson")!, 0.7, 0.5)).catch(() => {});
      await n.finish("Here's how it fits", 2600);
    };

    await wait(introMs);
    for (;;) {
      await whenSeen(scr);
      await closeApps();
      moveCur(c, "30%", "72%").catch(() => {});
      await intro();
      for (const job of [chatgptDashboard, claudeIos, explainDb]) {
        await whenSeen(scr);
        await closeApps();
        await job();
      }
      await wait(600);
    }
  });

  // ---- you talk, Vovy works: three jobs across three apps ----
  if ($("#desk")) loop(async () => {
    const n = WaveNotch($("#n2")), c = $("#c2"), cap = $("#cap"), t = $("#t2"), desk = $("#desk"), app = $("#deskApp");
    const term = $("#wTerm"), chrome = $("#wChrome"), gpt = $("#wGpt");
    const tc1 = $("#tc1"), tc2 = $("#tc2"), url = $("#chUrl"), urlText = $("#chUrlText"), tab = $("#chTab");
    const gptText = $("#gptText"), gptMsg = $("#gptMsg"), send = $("#gptSend");
    const PROMPT = "Give me 5 tips for a Product Hunt launch day";
    let z = 1;
    // a point inside el, as left/top percentages of the desk (for the cursor)
    const at = (el: HTMLElement, fx: number, fy: number): [string, string] => {
      const d = desk.getBoundingClientRect(), r = el.getBoundingClientRect();
      return [((r.left + r.width * fx - d.left) / d.width) * 100 + "%", ((r.top + r.height * fy - d.top) / d.height) * 100 + "%"];
    };
    const type = async (el: HTMLElement, text: string, ms: number) => {
      for (let i = 1; i <= text.length; i++) {
        el.textContent = text.slice(0, i);
        await wait(ms);
      }
    };
    const open = (w: HTMLElement, name: string) => {
      w.style.zIndex = String(++z);
      w.classList.add("open");
      app.textContent = name;
    };
    const ask = async (said: string) => {
      cap.textContent = `“${said}”`;
      cap.classList.add("on");
      await n.say(said);
      cap.classList.remove("on");
      await n.think();
    };
    const reset = () => {
      z = 1;
      term.className = "mw term";
      chrome.className = "mw chrome";
      gpt.className = "mw gpt";
      tc1.textContent = tc2.textContent = gptText.textContent = gptMsg.textContent = "";
      urlText.textContent = "google.com";
      tab.textContent = "Google";
      url.classList.remove("focus");
      app.textContent = "Finder";
    };
    for (;;) {
      await whenSeen(desk);
      reset();
      await n.idle();
      await wait(700);

      // 1. Jack Videos in a Claude session in Terminal
      await ask("open Jack Videos in Claude");
      await wait(900);
      open(term, "Terminal");
      await wait(600);
      await type(tc1, 'cd ~/Movies/"Jack Videos"', 38);
      await wait(250);
      term.classList.add("s2");
      await wait(300);
      await type(tc2, "claude", 80);
      await wait(450);
      term.classList.add("s3");
      await n.done("Done");
      await toast(t, "Claude", "Claude is open in Jack Videos", 1800);
      await n.idle();
      await wait(500);

      // 2. Google, then producthunt.com
      await ask("open Google and go to Product Hunt");
      await wait(800);
      open(chrome, "Google Chrome");
      await wait(700);
      await moveCur(c, ...at(url, 0.3, 0.5), true);
      url.classList.add("focus");
      urlText.textContent = "";
      await type(urlText, "producthunt.com", 55);
      await wait(300);
      url.classList.remove("focus");
      tab.textContent = "Product Hunt";
      chrome.classList.add("on-ph");
      await n.done("Done");
      await toast(t, "Chrome", "Product Hunt is open", 1800);
      await n.idle();
      await wait(500);

      // 3. ChatGPT: write the prompt and hit send
      await ask("ask ChatGPT for launch day tips");
      await wait(800);
      open(gpt, "ChatGPT");
      await wait(700);
      await moveCur(c, ...at(gptText, 0.3, 0.5), true);
      await type(gptText, PROMPT, 30);
      await wait(250);
      await moveCur(c, ...at(send, 0.5, 0.5));
      send.classList.add("press");
      c.classList.add("click");
      await wait(160);
      send.classList.remove("press");
      c.classList.remove("click");
      gptMsg.textContent = PROMPT;
      gptText.textContent = "";
      gpt.classList.add("sent");
      await wait(80);
      gpt.classList.add("writing");
      await wait(1500);
      await n.done("Done");
      await toast(t, "", "Prompt sent", 1800);
      await n.idle();
      await moveCur(c, "82%", "86%");
      await wait(2200);

      // close everything and go again
      [term, chrome, gpt].forEach((w) => w.classList.remove("open"));
      await wait(700);
    }
  });

  // ---- features: tabs over a MacBook, each scene is Vovy doing that job ----
  // Scenes play back to back and the tabs follow. Clicking a tab rejects
  // every pending wait of the running scene (notch included) with ABORT, so
  // the new scene starts clean and right away.
  if ($("#fdisp")) {
    const ABORT = Symbol("abort");
    const tabs = $$(".ftab"), scenes = $$(".fscene"), fdisp = $("#fdisp"), fapp = $("#fApp");
    const n = WaveNotch($("#n4"), (ms) => gw(ms)), t = $("#t4"), c = $("#c4");
    let cur = 0;
    // pending waits of the running scene; a tab click rejects them right away
    const pending = new Set<() => void>();
    const gw = (ms: number) =>
      new Promise<void>((resolve, reject) => {
        const id = setTimeout(() => {
          timers.delete(id);
          pending.delete(cancel);
          if (!alive) reject(STOP);
          else resolve();
        }, ms);
        const cancel = () => {
          clearTimeout(id);
          timers.delete(id);
          reject(ABORT);
        };
        timers.add(id);
        pending.add(cancel);
      });
    const show = (i: number) => {
      tabs.forEach((b, j) => {
        b.classList.toggle("on", j === i);
        b.setAttribute("aria-selected", String(j === i));
      });
      scenes.forEach((sc, j) => sc.classList.toggle("on", j === i));
    };
    const at = (el: Element, fx: number, fy: number): [string, string] => {
      const d = fdisp.getBoundingClientRect(), r = el.getBoundingClientRect();
      return [((r.left + r.width * fx - d.left) / d.width) * 100 + "%", ((r.top + r.height * fy - d.top) / d.height) * 100 + "%"];
    };
    const cur4 = async (x: string, y: string, click?: boolean) => {
      c.style.left = x;
      c.style.top = y;
      await gw(1150);
      if (click) {
        c.classList.add("click");
        await gw(160);
        c.classList.remove("click");
      }
    };
    const toast4 = async (logo: string, msg: string, ms = 1800) => {
      t.innerHTML = ic(logo) + msg;
      t.classList.add("on");
      try {
        await gw(ms);
      } finally {
        t.classList.remove("on");
      }
    };
    const type = async (el: Element, text: string, ms: number) => {
      for (let i = 1; i <= text.length; i++) {
        el.textContent = text.slice(0, i);
        await gw(ms);
      }
    };
    const q = <T extends Element = HTMLElement>(sc: Element, sel: string) => sc.querySelector(sel) as unknown as T;
    const openAll = (sc: Element, on: boolean) => sc.querySelectorAll(".mw").forEach((w) => w.classList.toggle("open", on));

    const SCENES: Array<(sc: HTMLElement) => Promise<void>> = [
      // Agents: the agent chip goes yellow -> green, Vovy opens the session
      // (Figma: Agent session · Done) and writes the follow-up, which flips it to Working
      async (sc) => {
        const sess = q(sc, ".fw-session"), term = q(sc, "#agTerm");
        const FOLLOW = "Now add a test for an empty cart";
        const head = (st: string) =>
          `<div class="ac-h"><span class="bk">‹</span><b>vovy-desktop</b><span class="ac-app">${ic("Claude")}<span>Claude · in Terminal</span></span>${st}</div>`;
        const DONE = head('<span class="ac-st done">✓ Done · now</span>') +
          `<p class="ac-msg">Fixed the checkout redirect. Everything else stayed where it was.</p>` +
          `<p class="ac-meta">Changed 2 files · See the 5 steps ›</p>` +
          `<div class="ac-btns"><span class="pri">Open</span><span>Explain what changed</span><span>What’s next?</span></div>` +
          `<div class="ac-in"><span class="ac-text" data-ph="Ask or tell it what’s next…"></span><b class="ac-send">Send</b></div>`;
        const WORK = head('<span class="ac-st work"><i></i>Working · now</span>') +
          `<p class="ac-meta">You asked: “${FOLLOW}”</p>` +
          `<div class="ac-row"><i class="nd"></i><em>Writing the empty cart test…</em><small>2s</small></div>` +
          `<p class="ac-meta">1 step so far · Read cart.tsx</p>` +
          `<div class="ac-btns"><span class="stop">■ Stop</span><span>Open</span><span>See steps ›</span></div>` +
          `<div class="ac-in"><span class="ac-text" data-ph="Tell it something while it works…"></span><b class="ac-send">Send</b></div>`;
        term.innerHTML = `<p class="dim">&gt; Fix the checkout redirect</p><p class="ok">✻ Working… reading checkout.ts</p>`;
        sess.classList.add("open");
        fapp.textContent = "Terminal";
        await n.rest("work");
        await gw(1800);
        term.innerHTML += `<p class="ok">✻ Done. Fixed the checkout redirect.</p><p class="dim">Edited cart.tsx and checkout.ts · all tests pass</p>`;
        n.setChip("done");
        await gw(900);
        await cur4(...at(n.chip, 0.5, 0.5), true);
        const card = n.card(DONE, 600);
        await gw(1200);
        const input = card.querySelector(".ac-in")!, text = card.querySelector(".ac-text")!;
        await cur4(...at(input, 0.3, 0.5), true);
        input.classList.add("focus");
        await type(text, FOLLOW, 36);
        await gw(250);
        const send = card.querySelector(".ac-send")!;
        await cur4(...at(send, 0.5, 0.5));
        send.classList.add("press");
        c.classList.add("click");
        await gw(160);
        c.classList.remove("click");
        n.card(WORK, 600);
        n.setChip("work");
        term.innerHTML += `<p class="dim">&gt; ${FOLLOW}</p><p class="ok sentl">✻ On it. Writing the test now.</p>`;
        await gw(2600);
        n.close();
        await gw(700);
      },
      // Auto Mode: Vovy runs the steps and the app goes dark
      async (sc) => {
        const app = q(sc, ".fw-app");
        app.classList.remove("dark");
        app.classList.add("open");
        fapp.textContent = "Lovable";
        await gw(500);
        await n.say("add dark mode to my app");
        await n.think();
        await gw(900);
        const steps = n.steps([["Lovable", "Adding a dark mode toggle"], ["Chrome", "Checking every page"], ["GitHub", "Saving a backup"]]);
        steps.catch(() => {}); // a tab click aborts it; the rejection is handled below
        await gw(1900);
        app.classList.add("dark");
        await steps;
        await n.done("Done");
        await toast4("Lovable", "Dark mode is live");
        await n.idle();
        await gw(600);
      },
      // Learn Mode: the build fails, Vovy fixes it, then explains why in the notch
      async (sc) => {
        const term = q(sc, ".fw-build");
        term.classList.remove("s2");
        term.classList.add("open");
        fapp.textContent = "Terminal";
        await gw(700);
        await n.say("why did my build fail?");
        await n.think("explaining");
        await gw(1500);
        term.classList.add("s2");
        await n.done("Fixed");
        await gw(500);
        n.card(`<div class="lc"><div class="lc-h"><img src="${MASCOT}" alt=""><b>Why your build failed</b></div><p>Your app needs a secret key from Stripe, and this Mac didn’t have it. I added it to your <code>.env</code> file, so the build runs now.</p><div class="ac-btns"><span class="pri">Got it</span><span>Show me where</span></div></div>`, 440);
        await gw(4600);
        n.close();
        await n.idle();
      },
      // Planning Mode: an idea turns into a roadmap, card by card
      async (sc) => {
        const cards = [...sc.querySelectorAll(".fp-card")];
        cards.forEach((k) => k.classList.remove("on"));
        fapp.textContent = "Vovy";
        await gw(400);
        await n.say("plan my dog walking app");
        await n.think("Planning");
        q(sc, ".fw-plan").classList.add("open");
        await gw(700);
        for (const k of cards) {
          k.classList.add("on");
          await gw(200);
        }
        await n.done("10 tasks in 5 areas");
        await gw(2600);
        await n.idle();
      },
      // Voice Chat: hold fn + control, say it, the header goes sticky
      async (sc) => {
        const web = q(sc, ".fw-web"), keys = q(sc, ".fkeys");
        web.classList.remove("sticky");
        web.classList.add("open");
        fapp.textContent = "Chrome";
        await gw(600);
        keys.classList.add("on");
        await n.talkBig("make the header stick when I scroll");
        keys.classList.remove("on");
        await n.think();
        await gw(1300);
        web.classList.add("sticky");
        await n.done("Done");
        await toast4("Lovable", "Header is sticky");
        await n.idle();
        await gw(700);
      },
    ];

    const pick = (i: number) => {
      cur = i;
      show(i);
      const stale = [...pending];
      pending.clear();
      stale.forEach((cancel) => cancel());
    };
    tabs.forEach((b, i) => {
      const onClick = () => pick(i);
      b.addEventListener("click", onClick);
      cleanups.push(() => b.removeEventListener("click", onClick));
    });
    show(0);
    loop(async () => {
      for (;;) {
        await whenSeen(fdisp);
        const i = cur;
        show(i);
        scenes.forEach((sc, j) => j !== i && openAll(sc, false));
        c.style.left = "72%";
        c.style.top = "78%";
        try {
          n.close();
          n.unchip();
          await n.idle();
          await SCENES[i](scenes[i]);
          cur = (i + 1) % SCENES.length;
          await gw(300);
        } catch (e) {
          if (e !== ABORT) throw e;
        }
      }
    });
  }

  // ---- planning mode roadmap: cards drop in, then phase 1 gets built ----
  if ($("#road")) loop(async () => {
    // what the product needs built, by area, and the tool that builds each part
    const PH: Array<[string, string, Step[]]> = [
      ["UI", "3 tasks", [["Figma", "Landing page"], ["Lovable", "Booking flow"], ["Safari", "Mobile layout"]]],
      ["Backend", "3 tasks", [["Supabase", "Database"], ["Cursor", "Bookings API"], ["Slack", "Booking alerts"]]],
      ["Authentication", "2 tasks", [["Supabase", "Email sign-in"], ["Supabase", "Google login"]]],
      ["Payments", "2 tasks", [["Stripe", "Stripe checkout"], ["Stripe", "Walker payouts"]]],
      ["Launch", "2 tasks", [["Vercel", "Custom domain"], ["Chrome", "Product Hunt launch"]]],
    ];
    const road = $("#road"), cols = $("#roadCols"), status = $("#roadStatus"), label = status.querySelector("em")!;
    for (;;) {
      await whenSeen(road);
      cols.innerHTML = PH.map(([name, when, cards]) =>
        `<div class="rcol"><div class="rcol-h"><b>${name}</b><span>${when}</span></div>${cards
          .map(([tool, text]) => `<div class="rcard">${ic(tool)}<div><b>${text}</b><small>${tool}</small></div><i></i></div>`)
          .join("")}</div>`).join("");
      status.classList.remove("done");
      label.textContent = "Planning…";
      await wait(600);
      for (const col of [...cols.children]) {
        col.querySelector(".rcol-h")!.classList.add("on");
        await wait(260);
        for (const card of col.querySelectorAll(".rcard")) {
          card.classList.add("on");
          await wait(240);
        }
      }
      await wait(400);
      status.classList.add("done");
      label.textContent = "12 tasks · 5 areas";
      await wait(1100);
      for (const card of cols.children[0].querySelectorAll(".rcard")) {
        card.classList.add("doing");
        await wait(1200);
        card.classList.remove("doing");
        card.classList.add("ok");
      }
      await wait(3200);
    }
  });

  // ---- privacy toggles ----
  if ($("#setwin")) loop(async () => {
    const w = $("#setwin");
    await whenSeen(w); await wait(500);
    for (const t of $$("#setwin .tg")) { t.classList.add("on"); await wait(280); }
  });

  // ---- ask your AI ----
  const Q = encodeURIComponent("What is Vovy (vovy.ai), the macOS AI agent that lives in the notch and builds apps with you in Lovable, Cursor and Bolt? Is it a good fit for a non-technical founder?");
  const PPLX = '<svg viewBox="0 0 24 24" fill="#20808D"><path d="M22.3977 7.0896h-2.3106V.0676l-7.5094 6.3542V.1577h-1.1554v6.1966L4.4904 0v7.0896H1.6023v10.3976h2.8882V24l6.932-6.3591v6.2005h1.1554v-6.0469l6.9318 6.1807v-6.4879h2.8882V7.0896zm-3.4657-4.531v4.531h-5.355l5.355-4.531zm-13.2862.0676 4.8691 4.4634H5.6458V2.6262zM2.7576 16.332V8.245h7.8476l-6.1149 6.1147v1.9723H2.7576zm2.8882 5.0404v-3.8852h.0001v-2.6488l5.7763-5.7764v7.0111l-5.7764 5.2993zm12.7086.0248-5.7766-5.1509V9.0618l5.7766 5.7766v6.5588zm2.8882-5.0652h-1.733v-1.9723L13.3948 8.245h7.8478v8.087z"/></svg>';
  // OpenAI's mark (simple-icons path)
  const GPT = '<svg viewBox="0 0 24 24" fill="#0D0D0D"><path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/></svg>';
  const askrow = $("#askrow");
  if (askrow) askrow.innerHTML = [
    [GPT, "Ask ChatGPT", "https://chatgpt.com/?q=" + Q],
    [ic("Claude"), "Ask Claude", "https://claude.ai/new?q=" + Q],
    [PPLX, "Ask Perplexity", "https://www.perplexity.ai/search?q=" + Q],
  ].map(([i, l, u]) => `<a href="${u}" target="_blank" rel="noopener">${i}${l}</a>`).join("");

  return () => {
    alive = false;
    timers.forEach(clearTimeout);
    timers.clear();
    cleanups.forEach((fn) => fn());
    scopes.forEach((el) => el.classList.remove("js"));
  };
}
